<template>
  <LoadingPage v-if="!hasData" />
  <main v-else class="container">
    <div
      v-if="doc.tags.includes('BETA')"
      class="beta-container"
      :class="{ 'slide-over-sesson': isSlideOverSession, 'slide-over-team': isSlideOverTeam }"
    >
      <img class="beta-sticker" src="../../assets/beta-sticker.svg" alt="Gyre BETA sticker" />
    </div>

    <Modal v-model="isShareModalOpen">
      <template #header>
        <form @submit.prevent="onSaveSharingClick" id="goal-sharing-form">
          <label for="allow-sharing">
            Allow link sharing?
            <input
              name="allow-sharing"
              type="checkbox"
              class="toggle-switch-checkbox"
              style="vertical-align: bottom; margin-left: 10px"
              v-model="isSharingEnabled"
              data-test="goal-sharing-toggle"
            />
          </label>
        </form>
      </template>
      <div class="goal-sharing-modal-body">
        <p>Anyone with this link can view:</p>
        <button
          v-if="!isLinkCopied"
          @click="onCopyLinkClick"
          type="button"
          class="btn btn-default-share"
          data-test="copy-goal-sharing-link"
        >
          Copy link
        </button>
        <button v-else @click="onCopyLinkClick" type="button" class="btn btn-default-tick">
          Copied!
        </button>
      </div>
      <template #footer>
        <div class="center-flex">
          <button @click="toggleShareModal" type="button" class="btn btn-default w-100">
            Close
          </button>
          <button
            type="submit"
            form="goal-sharing-form"
            class="btn btn-primary w-100"
            data-test="submit-goal-sharing"
          >
            {{ isSavingSharing ? "Saving..." : "Save" }}
          </button>
        </div>
        <p v-if="goalSharingError" class="text-danger text-center">{{ goalSharingError }}</p>
      </template>
    </Modal>

    <div class="team-image-container team-image">
      <img v-if="teamImgSrc" :src="teamImgSrc" alt="team image" />
    </div>
    <h1 class="text-center">
      <span data-test="goal-capture-title">{{ teamName }}</span>
      <br />
      {{ cms.asText(doc.data.title) }}
    </h1>
    <button
      v-if="!isSharedView && isTeamAdmin"
      @click="toggleShareModal"
      style="margin-top: 20px"
      class="btn btn-primary-share btn-sm"
      type="button"
      data-test="goal-share-button"
    >
      Share
    </button>
    <div class="goal-doc-section">
      <div v-html="introText" class="goal-doc-paragraph"></div>
    </div>
    <div>
      <div v-for="(goal, index) in teamGoals" :key="`${goal.goalName}-${index}`">
        <GoalArea
          :goal-index="index"
          :goal="goal"
          :cms-analyses="analyses"
          :suggestion-prompt="doc.data.suggestion_prompt.data.prompt"
          :locked="goal.locked"
          @update:lockStatus="unlockGoal"
          @update:goal="updateGoal"
          @deleteGoal="deleteGoal"
        />
      </div>
      <div v-if="canAddGoal" class="add-goal-container center-flex">
        <h3>Add Goal</h3>
        <button
          type="button"
          class="btn btn-primary-fab-plus"
          tabindex="0"
          @click="addGoal"
          @keyup.enter="addGoal"
          data-test="add-goal-button"
        ></button>
      </div>
    </div>
  </main>
</template>
<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from "vue";
import axios, { AxiosError } from "axios";
import { Modal } from "uiv";
import { useRoute } from "vue-router";
import {
  CmsDocument,
  CmsGoalCapturePage,
  CmsRichText,
} from "../../services/cms/cms-type-definitions";
import cms from "../../services/cms/cms";
import { csContext } from "../../services/cs-context";
import GoalArea from "../../components/goal-capture/goal-area.vue";
import { TeamGoalApiModel, TeamGoalsApiModel } from "../../api-models";
import { useAxios } from "../../composables/api";
import LoadingPage from "../loading-page.vue";
import { useToggle } from "../../composables/useToggle";
import goalCaptureStore from "../../stores/goal-capture-store";

export interface TeamGoal extends TeamGoalApiModel {
  locked: boolean;
}

const props = defineProps<{ doc: CmsDocument }>();
const doc = ref<CmsGoalCapturePage>(props.doc as CmsGoalCapturePage);

const route = useRoute();
const isSharedView = ref(route.query.isSharedLink);
const isSlideOverSession = ref(
  route.fullPath.includes("/resources/session") && route.fullPath.includes("sliding"),
);

const isSlideOverTeam = ref(
  route.fullPath.includes("/team/dashboard") && route.fullPath.includes("sliding"),
);

const { data, promise: getGoalsRequest } = useAxios<TeamGoalsApiModel>(
  `/api/teamgoals/${
    route.query.isSharedLink && route.query.teamId ? route.query.teamId : csContext.currentTeam?.id
  }`,
);

const isTeamAdmin = ref(false);
provide("isTeamAdmin", isTeamAdmin);
const error = ref("");
const teamGoals = ref<TeamGoal[]>([]);
const teamName = ref<string | null>(null);
const teamImgSrc = csContext.currentTeam?.imageUrl;

const analyses = computed(() => doc.value.data.analyses);
const hasData = computed(() => !!data.value && isTeamAdmin.value !== null);

const canAddGoal = computed(() => {
  return !!isTeamAdmin.value && teamGoals.value.every((goal) => goal.locked === true);
});

function refreshTeamGoals() {
  teamGoals.value = [...teamGoals.value];
}

function unlockGoal(index: number, status: boolean) {
  teamGoals.value[index].locked = status;
  refreshTeamGoals();
}

interface UpdateGoalModel {
  index: number;
  id: string;
  locked: boolean;
  goalName: string;
  createdById: string;
}

function updateGoal(index: number, goal: UpdateGoalModel) {
  teamGoals.value[index] = {
    ...teamGoals.value[index],
    id: goal.id,
    goalName: goal.goalName,
    locked: goal.locked,
    createdById: goal.createdById,
  };

  refreshTeamGoals();
}

function addGoal() {
  teamGoals.value.push({
    id: "",
    goalName: "",
    teamGoalAnalyses: [],
    createdDate: new Date().toISOString(),
    locked: false,
    createdById: "",
  });
}

async function deleteGoal(goalIndex: number) {
  try {
    const selectedGoal = teamGoals.value[goalIndex];
    if (selectedGoal?.id) {
      axios.delete(`/api/teamgoals/${selectedGoal.id}`);
    }
    teamGoals.value.splice(goalIndex, 1);
  } catch (e: unknown) {
    error.value = (e as AxiosError).message;
  }
}

const { state: isShareModalOpen, toggleState: toggleShareModal } = useToggle(false);
const { state: isLinkCopied, toggleState: toggleLinkCopied } = useToggle(false);
const isSharingEnabled = ref(false);
const goalSharingError = ref<string | null>(null);
const isSavingSharing = ref(false);

function onCopyLinkClick() {
  const goalDocumentLink = `${window.location.origin}/teamgoals?isSharedLink=true&teamId=${csContext.currentTeam?.id}`;
  // copy link to clipboard
  navigator.clipboard.writeText(goalDocumentLink);
  if (!isLinkCopied.value) {
    toggleLinkCopied();
  }
}

async function onSaveSharingClick() {
  if (isSavingSharing.value) {
    return;
  }

  goalSharingError.value = null;

  isSavingSharing.value = true;
  try {
    await axios.patch(`/api/teamsettings/patchteamgoalsharing/${isSharingEnabled.value}`);
    toggleShareModal();
  } catch (e: unknown) {
    goalSharingError.value = "There was an error saving, please try again later";
  } finally {
    isSavingSharing.value = false;
  }
}

function findAndReplaceUnknownTeamNameIfSharedDoc(text: CmsRichText): string {
  const textHtml = cms.asHtml(text);
  if (isSharedView.value) {
    if (teamName.value) {
      // find UNKNOWN_TEAM in the html and replace it with the team name
      return textHtml.replace(/<span>UNKNOWN_TEAM<\/span>/g, `<span>${teamName.value}</span>`);
    }
  }
  return textHtml;
}

const introText = computed(() => {
  return findAndReplaceUnknownTeamNameIfSharedDoc(doc.value.data.introduction);
});

onMounted(async () => {
  const response = await getGoalsRequest;
  isSharingEnabled.value = response.isTeamGoalSharingEnabled;
  teamName.value = response.teamName;
  if (response.teamGoals.length > 0) {
    teamGoals.value = response.teamGoals
      .map((goal) => ({
        id: goal.id,
        goalName: goal.goalName,
        createdDate: goal.createdDate,
        teamGoalAnalyses: goal.teamGoalAnalyses,
        locked: true,
        createdById: goal.createdById,
      }))
      .sort((a, b) => (new Date(a.createdDate) as any) - (new Date(b.createdDate) as any));
  } else {
    addGoal();
  }

  goalCaptureStore.initSuggestionsStore(teamGoals.value);

  if (!isSharedView.value) {
    const { data: isTeamLead } = useAxios<boolean>(
      `/api/grouproles/isgroupadmin/${csContext.currentTeam?.id ?? route.query.teamId}`,
    );
    watch(isTeamLead, (value) => {
      if (value !== null) {
        isTeamAdmin.value = value;
      }
    });
  }
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

@smart-text-box-offset: 40px;
@border-radius: 5px;

h1 {
  margin-top: 20px;
}

.goal-doc-section {
  margin: 20px 0 -30px;
}

.container {
  max-width: 750px;
  // position: relative;
}

.add-goal-container {
  height: 120px;
  background-color: @charismatic-color;
  margin: calc(@smart-text-box-offset + 20px) auto 0;
  text-align: center;
  padding: 20px;
  position: relative;
  border-radius: @border-radius;
  color: @body-bg;

  h3 {
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  button {
    position: absolute;
    bottom: -@smart-text-box-offset + 20px;
    left: 50%;
    transform: translateX(-50%);
  }
}

.goal-sharing-modal-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  gap: 20px;

  p {
    margin-bottom: 0;
  }
}

.w-100 {
  width: 100px;
}

.team-image {
  width: 150px;
  margin: 20px auto 0;
}

@sliding-nav-height: 77px;
@topnav-height: 51px;
@container-margin: 15px;
@sidenav-width: 72px;

.beta-container {
  position: absolute;
  top: @topnav-height;
  left: @sidenav-width;
  width: 180px;

  @media (max-width: @screen-xxs-max) {
    width: 120px;
    left: 0;
  }
}

.beta-sticker {
  width: 100%;
}

.slide-over-sesson {
  top: @sliding-nav-height;
  left: 0;
}

.slide-over-team {
  top: @sliding-nav-height;
  left: -@container-margin;
}
</style>
